import NiceModal from "@ebay/nice-modal-react";
import { Spin, Typography, Tooltip, Button } from "antd";
import React from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { DepositTransactionModalForm, TransactionViewMore, toast, AdminBankAccount, CustomerBankAccount, ColumnType, Amount } from "src/components";
import TransactionResultModal from "src/components/Modals/TransactionResultModal/TransactionResultModal";
import { AmountColumn } from "src/components/Table";
import { useDeleteTransaction } from "src/hooks";
import { GroupBank } from "src/types";
import Icon from "../../components/Icon/Icon";
import { Option } from "../../components/RadioButton/RadioButton";
import { OurTableProps } from "../../components/Table/Table";
import { isDepositTransaction, isWithdrawTransaction, mapToTransaction } from "../../mapper/mapToTransaction";
import { affiliateStateAtom, authState, loadingState, modalState } from "../../recoils";
import { requestChangeTransactionStatus } from "../../services/changeTransactionStatus";
import { cancelReserveDepositTransaction, reserveDepositTransaction } from "../../services/payment";
import { cancelReserveWithdrawTransaction, PostRetryAutoWithdraw, ReserveTransactionResponse, reserveWithdrawTransaction } from "../../services/payment/payment";
import { BaseResponse } from "../../services/service.type";
import { DEPOSIT_TRANSACTION_STATUS, PutManualWithdraw, WITHDRAW_TRANSACTION_STATUS } from "../../services/transactions/transactions";
import { decodeAuthToken } from "../../utils/decode/decode";
import {
  DEPOSIT_FILTER_HISTORY_TRANSACTIONS,
  DEPOSIT_FILTER_PENDING_TRANSACTIONS,
  getCallConfig,
  SELECTED_TAP,
  WITHDRAW_FILTER_HISTORY_TRANSACTIONS,
  WITHDRAW_FILTER_PENDING_TRANSACTIONS,
  WITHDRAW_PROCESSING_TRANSACTIONS
} from "./config";
import { TransactionStatus, Remark } from "./renderColumn";
import { DEPOSIT_STATUS, FILTER, SearchCriteria, SearchResult, SearchValue, Transaction, WITHDRAW_STATUS } from "./type";
import { getColumnFilters } from "src/utils/table";
import BonusColumn from "src/components/TableColumns/BonusTypeColumn";
import Label from "src/components/Text/Label";

export type AllColumnField = 'transferedAtDate' | 'transferedAtTime' | 'adminAccount' | 'customerAccount' | 'amount' | 'isBonus' | 'remark' | 'status' | 'moreDetail' | 'bonus';

export type TransactionState = {
  transactions: Transaction[];
  isTransactionsLoading: boolean;
  getSelectedTab: () => FILTER;
  getFilterOptions: () => Option[];
  getBaseTableProps: () => OurTableProps;
  depositingTransaction?: Transaction;
  withdrawingTransaction?: Transaction;
  refundRequestingTransaction?: Transaction;
  refundingTransaction?: Transaction;
  cancelingRefundTransaction?: Transaction;
  editingManualWithdraw?: Transaction;
  deletingManualWithdraw?: Transaction;
  editingManualDeposit?: Transaction;
  deletingManualDeposit?: Transaction;
  setDepositingTransaction: (_t?: Transaction) => void;
  searchCriteria: SearchCriteria;
  searchResult: SearchResult;
  setSearchCriteria: (_s: SearchCriteria) => void;
  searchValue: SearchValue
  setSearchValue: (_s: SearchValue) => void;
  doCallDepositTransactions: (_s: SearchCriteria) => void;
  doCallWithdrawTransaction: (_s: SearchCriteria) => void;
  reserveTransaction: (_transaction: Transaction, type?: ReserveType) => void;
  cancelReserveTransaction: (_transaction: Transaction, type?: ReserveType, is_closeModal?: boolean) => Promise<ReserveTransactionResponse>;
  requestCancelRefundDepositTransaction: (_transaction: Transaction) => Promise<BaseResponse | null>;
  setTransactions: (_transaction: Transaction[]) => void;
  setSearchResult: (_s: SearchResult) => void;
  getAllColumn: () => Record<AllColumnField, ColumnType<Transaction>>;
  setRetryAutoWithdrawingTransaction: (_t: Transaction) => void;
  doCallRetryAutoWithdraw: (_t: Transaction, groupBank: GroupBank) => void;
  showDoneTransactionModal: (_t: Transaction) => void;
};

export type IntialUseTransferState = {
  filter: FILTER;
};

export type ReserveType = DEPOSIT_STATUS | WITHDRAW_STATUS | 'EDIT_DEPOSIT' | 'DELETE_DEPOSIT' | 'EDIT_WITHDRAW' | 'DELETE_WITHDRAW';

export const useTransactionState = (initialState: IntialUseTransferState): TransactionState => {
  const [, setModal] = useRecoilState(modalState);
  const [, setLoading] = useRecoilState(loadingState);
  const [affiliateState] = useRecoilState(affiliateStateAtom);

  const [reserveType, setReserveType] = React.useState<ReserveType | undefined>(undefined);
  const [depositingTransaction, setDepositingTransaction] = React.useState<Transaction>();
  const [withdrawingTransaction, setWithdrawingTransaction] = React.useState<Transaction>();
  const [retryAutoWithdrawTransaction, setRetryAutoWithdrawingTransaction] = React.useState<Transaction>();
  const [refundRequestingTransaction, setRefundRequestingTransaction] = React.useState<Transaction>();
  const [refundingTransaction, setRefundingTransaction] = React.useState<Transaction>();
  const [cancelingRefundTransaction, setCancelingRefundTransaction] = React.useState<Transaction>();
  const [editingManualWithdraw, setEditingManualWithdraw] = React.useState<Transaction>();
  const [deletingManualWithdraw, setDeletingManualWithdraw] = React.useState<Transaction>();
  const [editingManualDeposit, setEditingManualDeposit] = React.useState<Transaction>();
  const [deletingManualDeposit, setDeletingManualDeposit] = React.useState<Transaction>();

  const { token = '' } = useRecoilValue(authState);

  const [isTransactionsLoading, setIsTransactionLoading] = React.useState<boolean>(true);
  const [transactions, setTransactions] = React.useState<Transaction[]>([]);
  const [searchCriteria, setSearchCriteria] = React.useState<SearchCriteria>({
    limit: initialState.filter === FILTER.PENDING_DEPOSIT ? 200 : 100,
    offset: 1,
    filter: initialState.filter,
    transferredAt: undefined,
  });

  const [searchResult, setSearchResult] = React.useState<SearchResult>({
    ...searchCriteria,
    totalPage: 0,
    totalTransaction: transactions.length,
    totalOtherTransaction: 0,
  })
  const [searchValue, setSearchValue] = React.useState<SearchValue>({
    value: ""
  })

  const { mutate: deleteTransaction } = useDeleteTransaction()

  React.useEffect(() => {
    // TO CONSIDER : might not use this when we move logic from reserverTransaction() to useEffect
    const authInfo = decodeAuthToken(token)
    const areYouDoingTransaction = withdrawingTransaction || retryAutoWithdrawTransaction
      || refundRequestingTransaction || refundingTransaction || cancelingRefundTransaction
      || editingManualWithdraw || deletingManualWithdraw
      || editingManualDeposit || deletingManualDeposit;
    if (areYouDoingTransaction) return;
    const myEditingTransaction = transactions.find(_transaction => _transaction.editingBy === authInfo.user_uid);
    if (myEditingTransaction?.status === DEPOSIT_STATUS.PENDING || myEditingTransaction?.status === WITHDRAW_STATUS.PENDING) {
      return;
    }
    const updateMyEditingTransaction = async (_transaction: Transaction) => {
      cancelReserveTransaction(_transaction);
    };

    if (myEditingTransaction) updateMyEditingTransaction(myEditingTransaction);
  }, [transactions])

  React.useEffect(() => {
    const authInfo = decodeAuthToken(token)
    const mySelectingTransaction = transactions.find(_transaction => _transaction.editingBy === authInfo.user_uid);
    // NOTE : Revert back realtime transaction
    // if (reserveType === DEPOSIT_STATUS.PENDING) {
    //   if (mySelectingTransaction?.status === DEPOSIT_STATUS.PENDING && depositingTransaction === undefined) {
    //     setDepositingTransaction(mySelectingTransaction);
    //     return;
    //   }

    // }
    // if (reserveType === 'EDIT_DEPOSIT') {
    //   if (mySelectingTransaction?.status === DEPOSIT_STATUS.PENDING && depositingTransaction === undefined) {
    //     setEditingManualDeposit(mySelectingTransaction);
    //     NiceModal.show(DepositTransactionModalForm, {
    //       transaction: mySelectingTransaction,
    //       onClose: () => cancelReserveTransaction(mySelectingTransaction, 'EDIT_DEPOSIT'),
    //       onSuccess: () => doCallDepositTransactions(searchCriteria),
    //     });
    //     return;
    //   }
    // }
    // if (reserveType === 'DELETE_DEPOSIT') {
    //   if (mySelectingTransaction?.status === DEPOSIT_STATUS.PENDING && depositingTransaction === undefined) {
    //     setDeletingManualDeposit(mySelectingTransaction);
    //     setModal({
    //       isModalVisible: true,
    //       type: 'warning',
    //       title: 'ลบรายการ',
    //       content: 'ต้องการลบรายการเติมเงินนี้ใช่หรือไม่',
    //       buttonType: 'question',
    //       onConfirm: () =>
    //         deleteTransaction(mySelectingTransaction.transactionId, {
    //           onSettled: () => cancelReserveTransaction(mySelectingTransaction, 'DELETE_DEPOSIT'),
    //           onSuccess: () => doCallDepositTransactions(searchCriteria),
    //         }),
    //       onCancel: () => cancelReserveTransaction(mySelectingTransaction)
    //     })
    //   }
    // }

    // NOTE : when we left transaction, we cancel it
    if (reserveType === undefined && mySelectingTransaction) { cancelReserveTransaction(mySelectingTransaction) }
  }, [transactions, reserveType])

  const getSelectedTab = (): FILTER => {
    return SELECTED_TAP[searchCriteria.filter];
  };
  const getFilterOptions = (): Option[] => {
    let options = DEPOSIT_FILTER_PENDING_TRANSACTIONS;
    switch (searchCriteria.filter) {
      case FILTER.PENDING_DEPOSIT:
      case FILTER.PENDING_REFUND:
        options = DEPOSIT_FILTER_PENDING_TRANSACTIONS
        break;
      case FILTER.ONLY_ME_DEPOSIT:
        options = DEPOSIT_FILTER_HISTORY_TRANSACTIONS;
        break;
      // case FILTER.ALL_DEPOSIT:
      //   options = DEPOSIT_FILTER_HISTORY_TRANSACTIONS.filter(o => ((o.role && scope[o.role]?.enable) || (o.role === undefined)));
      //   break;
      case FILTER.PENDING_WITHDRAW:
      case FILTER.REFUND:
        options = affiliateState.isAffiliateMode
          ? WITHDRAW_FILTER_PENDING_TRANSACTIONS.filter(o => o.value !== FILTER.REFUND)
          : WITHDRAW_FILTER_PENDING_TRANSACTIONS;
        break;
      case FILTER.WITHDRAW_PROCESSING:
        options = WITHDRAW_PROCESSING_TRANSACTIONS;
        break;
      case FILTER.ONLY_ME_WITHDRAW:
        options = WITHDRAW_FILTER_HISTORY_TRANSACTIONS;
        break;
      // case FILTER.ALL_WITHDRAW:
      //   options = WITHDRAW_FILTER_HISTORY_TRANSACTIONS.filter(o => ((o.role && scope[o.role]?.enable) || (o.role === undefined)));
      //   break;
      default:
        return [];
    }
    return options.map((option) => {
      if (option.value === searchCriteria.filter) {
        return {
          ...option,
          text: <>{option.text}&nbsp;{isTransactionsLoading ? <Spin size="small" /> : <>({searchResult.totalTransaction})</>}</>,
        }
      }
      return {
        ...option,
        text: <>{option.text}&nbsp;{isTransactionsLoading ? <Spin size="small" /> : <>({searchResult.totalOtherTransaction})</>}</>,
      };
    });

  };

  const showDoneTransactionModal = (_transaction: Transaction) => {
    const isDepositTransaciton = isDepositTransaction(_transaction);
    const isWithdrawTransaciton = isWithdrawTransaction(_transaction);
    setModal({
      isModalVisible: true,
      type: 'error',
      title: 'ไม่สามารถทำรายการได้',
      content: 'เนื่องจากรายการนี้มี Admin ท่านอื่นทำรายการอยู่',
      buttonType: 'confirm',
      onConfirm: () => {
        if (isDepositTransaciton) doCallDepositTransactions(searchCriteria);
        if (isWithdrawTransaciton) doCallWithdrawTransaction(searchCriteria);
      },
    });
  }

  const reserveTransaction = async (_transaction: Transaction, type?: ReserveType) => {
    const isDepositTransaciton = isDepositTransaction(_transaction);
    try {
      const reserveTransaction = isDepositTransaciton ? reserveDepositTransaction : reserveWithdrawTransaction;
      const response = await reserveTransaction({
        token,
        transaction_uid: _transaction.transactionId,
      });
      setReserveType(type || _transaction.status)
      if (response.status === 'SUCCESS') {
        // NOTE : move to useEffect [transaction, reserveType]
        if (type === DEPOSIT_STATUS.PENDING) {
          setDepositingTransaction(_transaction);
        } else if (type === 'EDIT_DEPOSIT') {
          setEditingManualDeposit(_transaction);
          NiceModal.show(DepositTransactionModalForm, {
            transaction: _transaction,
            onClose: () => cancelReserveTransaction(_transaction, 'EDIT_DEPOSIT'),
            onSuccess: () => doCallDepositTransactions(searchCriteria),
          });
        } else if (type === 'DELETE_DEPOSIT') {
          setDeletingManualDeposit(_transaction);
          setModal({
            isModalVisible: true,
            type: 'warning',
            title: 'ลบรายการ',
            content: 'ต้องการลบรายการเติมเงินนี้ใช่หรือไม่',
            buttonType: 'question',
            onConfirm: () =>
              deleteTransaction(_transaction.transactionId, {
                onSettled: () => cancelReserveTransaction(_transaction, 'DELETE_DEPOSIT'),
                onSuccess: () => doCallDepositTransactions(searchCriteria),
              }),
            onCancel: () => cancelReserveTransaction(_transaction)
          })
        }
        else if (type === 'EDIT_WITHDRAW') {
          setEditingManualWithdraw(_transaction);
        } else if (type === 'DELETE_WITHDRAW') {
          setDeletingManualWithdraw(_transaction);
          setModal({
            isModalVisible: true,
            type: 'warning',
            title: 'ลบรายการ',
            content: 'ต้องการลบรายการถอนเงินนี้ใช่หรือไม่',
            buttonType: 'question',
            onConfirm: async () => {
              try {
                const response = await PutManualWithdraw({
                  token,
                  transaction_uid: _transaction.transactionId,
                  amount: _transaction.amount,
                  status: WITHDRAW_TRANSACTION_STATUS.DELETED,
                });
                if (response.service_code === 'BOE-2000') {
                  toast.success('ลบรายการถอนเงินสำเร็จ')
                } else {
                  toast.error('ลบรายการถอนเงินไม่สำเร็จ')
                }
                doCallWithdrawTransaction(searchCriteria);
              } catch {
                toast.error('ลบรายการถอนเงินไม่สำเร็จ')
                doCallWithdrawTransaction(searchCriteria);
              }
              // Call delete withdraw
            },
            onCancel: () => cancelReserveTransaction(_transaction)
          })
        } else if (type === WITHDRAW_STATUS.PENDING) {
          setWithdrawingTransaction(_transaction);
        } else if (type === DEPOSIT_STATUS.REJECTED) {
          setRefundRequestingTransaction(_transaction);
        } else if (type === DEPOSIT_STATUS.CANCEL_REJECT) {
          setCancelingRefundTransaction(_transaction);
        } else if (_transaction.status === DEPOSIT_STATUS.WAITING_TRANSFER) {
          setRefundingTransaction(_transaction);
        }
      } else {
      }
    } catch (e) {
      showDoneTransactionModal(_transaction)
    }
  };

  // is_closeModal : flag for event close modal
  const cancelReserveTransaction = async (_transaction: Transaction, type?: ReserveType, is_closeModal?: boolean): Promise<ReserveTransactionResponse> => {
    const isDepositTransaciton = isDepositTransaction(_transaction);
    const cancelReserve = isDepositTransaciton ? cancelReserveDepositTransaction : cancelReserveWithdrawTransaction;
    const response = await cancelReserve({
      token,
      transaction_uid: _transaction.transactionId,
    });
    setReserveType(undefined);
    if (type === 'EDIT_DEPOSIT') setEditingManualDeposit(undefined);
    if (type === 'DELETE_DEPOSIT') setDeletingManualDeposit(undefined);
    if (type === 'EDIT_WITHDRAW') setEditingManualWithdraw(undefined);
    if (type === 'DELETE_WITHDRAW') setDeletingManualWithdraw(undefined);
    // if (type === DEPOSIT_STATUS.PENDING) setDepositingTransaction(undefined);
    if (type === WITHDRAW_STATUS.PENDING) setWithdrawingTransaction(undefined);
    if (type === DEPOSIT_STATUS.REJECTED) setRefundRequestingTransaction(undefined);
    if (type === DEPOSIT_STATUS.CANCEL_REJECT) setCancelingRefundTransaction(undefined);
    if (_transaction.status === DEPOSIT_STATUS.WAITING_TRANSFER) setRefundingTransaction(undefined);
    return response;
  };

  const requestCancelRefundDepositTransaction = async (_transaction: Transaction) => {
    const response = await requestChangeTransactionStatus({
      transaction_uid: _transaction.transactionId,
      transaction_status: DEPOSIT_TRANSACTION_STATUS.PENDING,
      token
    });

    return response;
  }

  const getAllColumn = (): Record<AllColumnField, ColumnType<Transaction>> => ({
    transferedAtDate: {
      title: 'วันที่',
      dataIndex: 'transferAtDate',
      key: "transferAtDate",
      width: 111,
    },
    transferedAtTime: {
      title: 'เวลาสลิป',
      dataIndex: 'transferAtTime',
      key: 'transferAtTime',
      filterSearch: true,
      filters: getColumnFilters("transferAtTime", transactions || []),
      onFilter: (value, row) => row.transferAtTime == value.toString(),
      width: 100,
    },
    adminAccount: {
      title: 'บัญชี PIGSPIN',
      key: 'adminBank',
      ellipsis: true,
      width: 200,
      render: (_: any, record: Transaction) => {
        return (
          <AdminBankAccount transaction={record} />
        )
      },
    },
    customerAccount: {
      title: 'บัญชีลูกค้า',
      key: 'customerBank',
      dataIndex: 'customerBankNumber',
      ellipsis: true,
      filterSearch: true,
      filters: getColumnFilters("customerBankNumber", transactions || []),
      onFilter: (value, row) => row.customerBank.accountNumber == value,
      width: 200,
      render: (_: any, record: Transaction) => {
        return (
          <CustomerBankAccount transaction={record} />
        )
      },
    },
    amount: {
      title: 'จำนวนเงิน',
      dataIndex: 'amount',
      key: 'amount',
      filterSearch: true,
      filters: getColumnFilters("amount", transactions || []),
      onFilter: (value, row) => row.amount == value,
      width: 120,
      render: (value: number, transaction: Transaction) => {
        return <AmountColumn {...transaction} />
      },
    },
    isBonus: {
      title: <div style={{ textAlign: 'center' }}>
        <Tooltip placement="top" title="รับโบนัส">
          <Button type="text" style={{ padding: 0 }}>
            <Icon name="outline-tag" size="1rem" />
          </Button>
        </Tooltip>
      </div>,
      dataIndex: 'isBonus',
      key: 'isBonus',
      width: 50,
      align: "center",
      render: (isBonus, record) => {
        return <BonusColumn isBonus={record.isBonus} bonusType={record.bonusType} />
      },
    },
    remark: {
      title: 'หมายเหตุ',
      dataIndex: 'remark',
      key: 'remark',
      width: 165,
      render: (_, record) => {
        return <Remark {...record} />
      }
    },
    status: {
      title: 'สถานะ',
      key: 'statusMessage',
      filterSearch: true,
      filters: getColumnFilters("statusMessage", transactions || []),
      onFilter: (value, row) => row.statusMessage == value.toString(),
      width: 140,
      render: (_: any, record: Transaction) => {
        return <TransactionStatus {...record} />
      }
    },
    moreDetail: {
      title: '',
      dataIndex: 'more',
      key: 'more',
      width: 50,
      render: (_: any, row: Transaction) => <TransactionViewMore transaction={row} />,
    },
    bonus: {
      title: "โบนัส",
      key: "bonus",
      width: 120,
      render: (value, record) => {
        return (record.isBonus && record.bonus === 0) ? <Label>(รับโบนัสเกินกำหนดต่อวัน)</Label> : <Amount value={record.bonus} />
      },
    },
  })

  const getBaseTableProps = () => {
    return {
      isLoading: isTransactionsLoading,
      isEmpty: searchResult.totalTransaction === 0 && searchResult.totalOtherTransaction === 0,
      isSearchNotFound: searchResult.totalTransaction === 0 && searchResult.totalOtherTransaction > 0,
      dataSource: transactions,
      totalItem: searchResult.totalTransaction,
      limit: searchResult.limit,
      offset: searchResult.offset,
      onPaginationChange: (offset: number, limit: number) => {
        setSearchCriteria({
          ...searchCriteria,
          offset,
          limit,
        })
      }
    }
  }

  const doCallDepositTransactions = async (_searchCriteria: SearchCriteria) => {
    setIsTransactionLoading(true);
    const { directCall, otherTabCall } = getCallConfig(token, _searchCriteria, affiliateState);
    const promise1 = directCall.call(directCall.payload);
    const promise2 = otherTabCall.call(otherTabCall.payload);
    const [res, otherRes] = await Promise.all([promise1, promise2]);
    const response = res?.data;
    const otherResponse = otherRes?.data;

    if (response && response?.status === 'SUCCESS') {
      const { data } = response;
      setTransactions(response?.data.results.map((row) => mapToTransaction(row)) || [])
      setSearchResult({
        ...searchResult,
        ..._searchCriteria,
        limit: data.pageSize,
        offset: data.page,
        totalPage: data.total / data.pageSize,
        totalOtherTransaction: otherResponse ? otherResponse.data.total : 0,
        totalTransaction: data.total,
      })
    } else {
      setTransactions([])
      setSearchResult({
        ...searchResult,
        totalPage: 0,
        totalTransaction: 0,
        totalOtherTransaction: otherResponse ? otherResponse.data.total : 0,
      });
    }
    setIsTransactionLoading(false);
  };

  const doCallWithdrawTransaction = async (_searchCriteria: SearchCriteria) => {
    setIsTransactionLoading(true);
    const { directCall, otherTabCall } = getCallConfig(token, _searchCriteria, affiliateState);
    const promise1 = directCall.call(directCall.payload);
    const promise2 = otherTabCall.call(otherTabCall.payload);
    const [res, otherRes] = await Promise.all([promise1, promise2]);
    const response = res?.data;
    const otherResponse = otherRes?.data;
    const totalOtherTransaction = otherResponse ? otherResponse.data.total : 0;

    if (response?.status === 'SUCCESS') {
      const { data } = response;
      setTransactions(response?.data.results.map((row) => mapToTransaction(row)) || [])
      setSearchResult({
        ...searchResult,
        ..._searchCriteria,
        limit: data.pageSize,
        offset: data.page,
        totalPage: data.total / data.pageSize,
        totalOtherTransaction,
        totalTransaction: data.total,
      })
    } else {
      setTransactions([])
      setSearchResult({
        ...searchResult,
        totalPage: 0,
        totalTransaction: 0,
        totalOtherTransaction,
      });
    };
    setIsTransactionLoading(false);
  }

  const doCallRetryAutoWithdraw = async (_transaciton: Transaction, groupBank: GroupBank) => {
    setLoading({ isLoading: true })
    const pushToManualFlow = () => {

      setModal({
        isModalVisible: true,
        type: 'error',
        title: 'ไม่สามารถโอนเงินด้วยระบบอัตโนมัติได้',
        content: (
          <>
            <div>ต้องการโอนเงินผ่านแอปธนาคารใช่หรือไม่</div>
          </>
        ),
        buttonType: 'question',
        confirmText: 'ใช่ ต้องการ',
        onConfirm: () => {
          reserveTransaction(_transaciton, WITHDRAW_STATUS.PENDING);
        },
        onCancel: () => {
          cancelReserveTransaction(_transaciton, undefined, true);
        },
      });
    };
    try {
      const response = await PostRetryAutoWithdraw({
        token,
        transactionId: _transaciton.transactionId,
        groupBankCode: groupBank.bankCode,
        groupBankName: groupBank.groupBankName,
      });
      setLoading({ isLoading: false })
      if (response.service_code === 'BOE-2000' && response.data) {
        cancelReserveTransaction(_transaciton);
        toast.success('บันทึกสำเร็จ');
        const { data } = response;
        NiceModal.show(TransactionResultModal, {
          type: "success",
          title: (
            <>
              โอนเงินอัตโนมัติสำเร็จ
              <br />
              <Typography.Text style={{ fontSize: 16, fontWeight: 500 }}>
                จากบัญชี PIGSPIN {data.from_bank_code} {data.from_account_name} {data.from_account_number}
              </Typography.Text>
            </>
          ),
          topicMessageBox: "ข้อความแจ้งลูกค้า",
          message: `โอนเงินไปยังลูกค้า ${data.to_bank_code} ${data.to_account_number}\nจำนวน ${response.data.amount_transferred} บาท  เรียบร้อยแล้วค่ะ`,
        })
        doCallWithdrawTransaction(searchCriteria);
      } else {
        setLoading({ isLoading: false })
        cancelReserveTransaction(_transaciton);
        const isAlreadyWithdraw = response.service_code === 'BOE-4002';
        const isCannotAutoWithdraw = response.service_code === 'BOE-4003';
        const isBankNotMatch = response.service_code === 'BOE-4004';
        if (isAlreadyWithdraw) {
          setModal({
            isModalVisible: true,
            type: 'error',
            title: 'ไม่สามารถทำรายการได้',
            content: 'เนื่องจากรายการนี้ถูกดำเนินการแล้ว',
            buttonType: 'confirm',
          })
        } else {
          pushToManualFlow();
        }
      }
      setRetryAutoWithdrawingTransaction(undefined);
    } catch (e: any) {
      cancelReserveTransaction(_transaciton);
    };
    setLoading({ isLoading: false })
    setRetryAutoWithdrawingTransaction(undefined);
  };

  return {
    transactions,
    isTransactionsLoading,
    depositingTransaction,
    withdrawingTransaction,
    refundRequestingTransaction,
    refundingTransaction,
    cancelingRefundTransaction,
    editingManualDeposit,
    deletingManualDeposit,
    editingManualWithdraw,
    deletingManualWithdraw,
    getSelectedTab,
    getFilterOptions,
    getBaseTableProps,
    setDepositingTransaction,
    searchValue,
    setSearchValue,
    searchCriteria,
    searchResult,
    setSearchCriteria,
    doCallDepositTransactions,
    doCallWithdrawTransaction,
    reserveTransaction,
    cancelReserveTransaction,
    requestCancelRefundDepositTransaction: requestCancelRefundDepositTransaction,
    setTransactions,
    setSearchResult,
    getAllColumn,
    setRetryAutoWithdrawingTransaction,
    doCallRetryAutoWithdraw,
    showDoneTransactionModal,
  };
};
